const COOKIE_DOMAIN = process.env.COOKIE_DOMAIN;
const NODE_ENV = process.env.NODE_ENV;

/**
 * Environment flags indicating the current runtime environment.
 * Used to conditionally enable/disable features based on environment.
 */
export type EnvFlags = {
  /** True when NODE_ENV is 'development' */
  isDev: boolean;
  /** True when NODE_ENV is 'production' */
  isProd: boolean;
  /** True when COOKIE_DOMAIN is 'localhost' or '127.0.0.1' */
  isLocalhost: boolean;
  /** True when COOKIE_DOMAIN is 'elevatormag.com' (production domain) */
  isProductionDomain: boolean;
  /** True when in development mode OR not on production domain. Used to enable test/dev features. */
  isTestMode: boolean;
};

/**
 * Checks the current environment configuration and returns flags indicating the runtime environment.
 * Determines environment state based on NODE_ENV and COOKIE_DOMAIN environment variables.
 *
 * Test mode (`isTestMode`) is active when:
 * - NODE_ENV is 'development', OR
 * - COOKIE_DOMAIN is not 'elevatormag.com' (any non-production domain)
 *
 * This allows test/dev features to be enabled in both local development and staging/test environments.
 *
 * @returns Environment flags object containing boolean flags for various environment states
 *
 * @example
 * ```ts
 * const { isTestMode, isLocalhost } = checkEnv();
 *
 * if (isTestMode) {
 *   // Enable debug logging or test features
 * }
 *
 * if (isLocalhost) {
 *   // Use local development API endpoints
 * }
 * ```
 */
export const checkEnv = (): EnvFlags => {
  const isDev = NODE_ENV === 'development';
  const isProd = NODE_ENV === 'production';
  const isProductionDomain = COOKIE_DOMAIN === 'elevatormag.com';
  const isLocalhost = COOKIE_DOMAIN === 'localhost' || COOKIE_DOMAIN === '127.0.0.1';
  const isTestMode = isDev || !isProductionDomain;

  return {
    isDev,
    isProd,
    isLocalhost,
    isProductionDomain,
    isTestMode,
  };
};
